/**
 * Shopify Variant Split - Exibe variações de cor como cards separados
 * Compatível com qualquer tema Shopify
 * Sem dependências externas, sem apps, sem Liquid customizado
 */

'use strict';

(function () {
  // ============================================
  // CONFIGURAÇÃO
  // ============================================
  const CONFIG = {
    colorOptionName: 'Cor',                                         // Nome EXATO da opção no Shopify Admin (case sensitive)
    productCardSelector: '.prod-card',                              // Seletor específico do tema CIA Pijamas
    collectionPageSelector: '.prod-grid',
    homeCollectionSelector: '[data-section-type="featured-collection"]',
    homeSliderTrackSelector: '.slider-track',
    variantParam: 'variant',                                       // query param para pré-seleção de variante
    maxParallelRequests: 5,                                        // max concurrent API calls
    batchSize: 5,                                                  // cards processados por batch
    colorVariantsCache: new Map(),                                 // cache de variantes por handle
    productRequestCache: new Map(),                                // cache de requisições em andamento por handle
    imageSize: 400,                                                // largura da imagem otimizada em pixels
    viewportRootMargin: '320px 0px',                              // inicia antes de entrar na tela para evitar pop-in
    viewportRootMarginCatalog: '80px 0px',                        // catalogo: carrega mais perto do viewport
    initialColorCardsLimit: 6,                                    // quantidade total de cards de cor no carregamento inicial
    initialColorCardsLimitCatalog: 3,                             // catalogo/search: reduz custo inicial de split
    deferredInsertBatchSize: 3,                                   // quantidade de cards inseridos por ciclo durante scroll
    deferredRootMargin: '420px 0px',                              // ativa insercao progressiva antes do usuario alcancar o fim
    deferredRootMarginCatalog: '120px 0px',                       // catalogo: insercao progressiva mais conservadora
    catalogCardsPerPage: 12,                                      // janela visual do catalogo apos split de cores
    enableDebug: false,
  };

  // ============================================
  // UTILITÁRIOS
  // ============================================

  /**
   * Log com namespace
   */
  function log(message, data = null) {
    if (!CONFIG.enableDebug) return;
    const prefix = '[VariantSplit]';
    if (data) {
      console.log(`${prefix} ${message}`, data);
    } else {
      console.log(`${prefix} ${message}`);
    }
  }

  const STATE = {
    initialColorCardsBudget: CONFIG.initialColorCardsLimit,
    renderedColorCardsCount: 0,
    deferredVariantQueue: [],
    deferredCardsBuffer: [],
    deferredSentinel: null,
    deferredObserver: null,
    deferredSentinelVisible: false,
    isDrainingDeferredQueue: false,
    isDeferredUnlocked: false,
    hasDeferredUnlockListener: false,
    catalogVisibleLimit: CONFIG.catalogCardsPerPage,
    hasCatalogLoadMoreBridge: false,
  };

  const CATALOG_HIDDEN_CLASS = 'variant-split-page-hidden';

  function isCatalogTemplate() {
    return document.body.classList.contains('template-collection') ||
      document.body.classList.contains('template-search');
  }

  function isOffersCollectionPage() {
    const path = (window.location && window.location.pathname ? window.location.pathname : '').toLowerCase();
    return /\/collections\/ofertas(?:\/|$)/.test(path);
  }

  function getVariantDiscountCents(variant) {
    if (!variant) return 0;
    const compare = Number(variant.compare_at_price || 0);
    const price = Number(variant.price || 0);
    if (!compare || compare <= price) return 0;
    return compare - price;
  }

  // Mesmo formato do formatMoney do theme.js, para o card dividido nao
  // destoar do resto da loja.
  function formatBRL(cents) {
    return 'R$ ' + (Number(cents) / 100).toFixed(2).replace('.', ',');
  }

  function resolveColorOptionIndex(product) {
    if (!product || !Array.isArray(product.options)) return -1;

    return product.options.findIndex((option) => {
      const optionName = typeof option === 'string'
        ? option
        : (option && option.name ? option.name : '');

      const normalized = String(optionName || '').trim().toLowerCase();
      return normalized === 'cor' || normalized === 'color';
    });
  }

  function resolveInitialColorCardsLimit() {
    return isCatalogTemplate() ? CONFIG.initialColorCardsLimitCatalog : CONFIG.initialColorCardsLimit;
  }

  function resolveViewportRootMargin() {
    return isCatalogTemplate() ? CONFIG.viewportRootMarginCatalog : CONFIG.viewportRootMargin;
  }

  function resolveDeferredRootMargin() {
    return isCatalogTemplate() ? CONFIG.deferredRootMarginCatalog : CONFIG.deferredRootMargin;
  }

  function getCatalogGrid() {
    if (!isCatalogTemplate()) return null;
    return document.getElementById('ProductGrid');
  }

  function getCatalogCardsForPaging() {
    const grid = getCatalogGrid();
    if (!grid) return [];

    return Array.from(grid.querySelectorAll(CONFIG.productCardSelector)).filter((card) => {
      if (!card || !card.isConnected) return false;
      if (card.style.display === 'none') return false;
      return true;
    });
  }

  function hasPendingCatalogSplitWork() {
    if (!isCatalogTemplate()) return false;

    if (STATE.deferredCardsBuffer.length > 0) return true;
    if (STATE.deferredVariantQueue.length > 0) return true;

    const grid = getCatalogGrid();
    if (!grid) return false;

    return Array.from(grid.querySelectorAll(CONFIG.productCardSelector)).some((card) => {
      if (!card || !card.isConnected) return false;
      if (card.getAttribute('data-variant-processed') === 'true') return false;
      return card.style.display !== 'none';
    });
  }

  function updateCatalogLoadMoreButton() {
    if (!isCatalogTemplate()) return;

    const loadMoreBtn = document.getElementById('load-more-products-btn');
    if (!loadMoreBtn) return;

    const hiddenCount = document.querySelectorAll(`#ProductGrid .${CATALOG_HIDDEN_CLASS}`).length;
    const hasNextUrl = !!(loadMoreBtn.dataset.nextUrl && loadMoreBtn.dataset.nextUrl.trim());
    const hasPendingSplit = hasPendingCatalogSplitWork();

    if (hiddenCount > 0 || hasPendingSplit) {
      loadMoreBtn.hidden = false;
      loadMoreBtn.disabled = false;
      loadMoreBtn.textContent = 'Carregar mais produtos';
      return;
    }

    if (!hasNextUrl) {
      loadMoreBtn.remove();
      return;
    }

    loadMoreBtn.hidden = false;
  }

  function applyCatalogPageWindow() {
    if (!isCatalogTemplate()) return;

    const cards = getCatalogCardsForPaging();
    if (!cards.length) {
      updateCatalogLoadMoreButton();
      return;
    }

    cards.forEach((card, index) => {
      const shouldShow = index < STATE.catalogVisibleLimit;
      card.classList.toggle(CATALOG_HIDDEN_CLASS, !shouldShow);
      card.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    });

    updateCatalogLoadMoreButton();
  }

  function revealNextCatalogCards(step = CONFIG.catalogCardsPerPage) {
    if (!isCatalogTemplate()) return false;

    const cards = getCatalogCardsForPaging();
    if (!cards.length) return false;

    const hiddenCount = Math.max(0, cards.length - STATE.catalogVisibleLimit);
    if (hiddenCount <= 0) return false;

    STATE.catalogVisibleLimit += Math.max(1, step);
    applyCatalogPageWindow();
    return true;
  }

  function ensureCatalogLoadMoreBridge() {
    if (STATE.hasCatalogLoadMoreBridge || !isCatalogTemplate()) return;
    STATE.hasCatalogLoadMoreBridge = true;

    window.addEventListener('variantSplit:loadMoreRequested', (event) => {
      const step = event && event.detail && Number(event.detail.pageSize)
        ? Number(event.detail.pageSize)
        : CONFIG.catalogCardsPerPage;

      const revealed = revealNextCatalogCards(step);
      if (revealed && event && typeof event.preventDefault === 'function') {
        event.preventDefault();
        return;
      }

      if (!hasPendingCatalogSplitWork()) {
        return;
      }

      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
      }

      unlockDeferredLoading();

      const grid = getCatalogGrid();
      const pendingCards = grid
        ? Array.from(grid.querySelectorAll(CONFIG.productCardSelector)).filter((card) => {
            if (!card || !card.isConnected) return false;
            if (card.getAttribute('data-variant-processed') === 'true') return false;
            return card.style.display !== 'none';
          }).slice(0, CONFIG.batchSize)
        : [];

      if (pendingCards.length) {
        processCardsInBatches(pendingCards, { mode: 'visible' }).then(() => {
          applyCatalogPageWindow();
          window.dispatchEvent(new CustomEvent('variantSplit:syncLoadMoreButton'));
        });
        return;
      }

      if (STATE.deferredVariantQueue.length > 0) {
        runWhenIdle(() => {
          drainDeferredQueue(step);
          applyCatalogPageWindow();
          window.dispatchEvent(new CustomEvent('variantSplit:syncLoadMoreButton'));
        });
      }
    });

    window.addEventListener('variantSplit:syncLoadMoreButton', () => {
      applyCatalogPageWindow();
    });
  }

  /**
   * Otimiza URL de imagem do Shopify adicionando parâmetros de resize
   * @param {string} url - URL original da imagem
   * @param {number} width - largura desejada em pixels
   * @returns {string} URL otimizada
   */
  function optimizeImageUrl(url, width = 400) {
    if (!url) return '';
    
    // Se a URL já tem parâmetros de tamanho, remove
    const cleanUrl = url.split('?')[0].replace(/_\d+x\d*\.(jpg|jpeg|png|webp|gif)/i, '.$1');
    
    // Extrai a extensão do arquivo
    const extension = cleanUrl.match(/\.(jpg|jpeg|png|webp|gif)$/i)?.[0] || '.jpg';
    const baseUrl = cleanUrl.replace(extension, '');
    
    // Adiciona parâmetro de resize do Shopify CDN (ex: _400x significa largura 400px)
    return `${baseUrl}_${width}x${extension}`;
  }

  function normalizeProductHandle(rawHandle) {
    if (!rawHandle || typeof rawHandle !== 'string') return null;

    const cleaned = decodeURIComponent(rawHandle)
      .trim()
      .replace(/^\/+|\/+$/g, '')
      .split('?')[0]
      .split('#')[0]
      .split('/')[0]
      .toLowerCase();

    if (!/^[a-z0-9][a-z0-9-]*$/.test(cleaned)) {
      return null;
    }

    return cleaned;
  }

  function extractProductHandle(card) {
    if (!card) return null;

    const explicitHandle = normalizeProductHandle(card.getAttribute('data-product-handle'));
    if (explicitHandle) {
      return explicitHandle;
    }

    const linkElement = card.querySelector('a[href*="/products/"]');
    if (!linkElement) {
      return null;
    }

    try {
      const url = new URL(linkElement.href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);
      return normalizeProductHandle(match ? match[1] : '');
    } catch (error) {
      return null;
    }
  }

  /**
   * Busca dados do produto via AJAX API do Shopify
   * @param {string} handle - handle do produto
   * @returns {Promise<Object>} objeto do produto com variantes
   */
  async function fetchProductVariants(handle) {
    if (!handle) return null;

    if (CONFIG.productRequestCache.has(handle)) {
      return CONFIG.productRequestCache.get(handle);
    }

    const request = (async () => {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (response.status === 404) {
        log(`Produto nao encontrado para handle: ${handle}`);
        return null;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error(`[VariantSplit] Erro ao buscar ${handle}:`, error);
      return null;
    } finally {
      CONFIG.productRequestCache.delete(handle);
    }
    })();

    CONFIG.productRequestCache.set(handle, request);
    return request;
  }

  /**
   * Filtra variantes únicas por cor
   * @param {Object} product - objeto do produto
   * @returns {Array} [{color, variantId, image, price, compare_at_price, available}]
   */
  function getColorVariants(product) {
    if (!product || !product.variants) return [];

    // Se houver apenas 1 variante, não expande
    if (product.variants.length <= 1) {
      return [];
    }

    // Encontra o índice da opção de cor (Cor/Color)
    const colorOptionIndex = resolveColorOptionIndex(product);

    // Se não houver opção de cor, retorna vazio
    if (colorOptionIndex === -1) {
      return [];
    }

    const isOffersMode = isOffersCollectionPage();

    // Agrupa variantes por cor
    const colorMap = new Map();

    product.variants.forEach((variant) => {
      // Pega o valor da cor para essa variante
      const colorValue = variant.option_values?.[colorOptionIndex]?.value ||
                         variant.options?.[colorOptionIndex] ||
                         null;

      if (!colorValue) {
        return;
      }

      // IGNORA variantes esgotadas
      if (!variant.available) {
        return;
      }

      if (!colorMap.has(colorValue)) {
        colorMap.set(colorValue, []);
      }
      colorMap.get(colorValue).push(variant);
    });

    const colors = [];

    colorMap.forEach((variants, colorValue) => {
      const saleVariants = variants.filter((variant) => getVariantDiscountCents(variant) > 0);

      if (isOffersMode && saleVariants.length === 0) {
        return;
      }

      const candidatePool = saleVariants.length ? saleVariants : variants;
      const chosenVariant = candidatePool.sort((a, b) => getVariantDiscountCents(b) - getVariantDiscountCents(a))[0];
      if (!chosenVariant) return;

      const image = chosenVariant.featured_image || product.featured_image;

      colors.push({
        color: colorValue,
        variantId: chosenVariant.id,
        image: image,
        price: chosenVariant.price,
        compare_at_price: chosenVariant.compare_at_price,
        available: chosenVariant.available,
        sku: chosenVariant.sku,
      });
    });

    return colors;
  }

  /**
   * Cria um novo card clonando o card original
   * @param {Object} product - objeto do produto (dados da API)
   * @param {Object} variantData - dados da variante (color, variantId, image, price, etc)
   * @param {HTMLElement} originalCard - elemento do card original para clonar
   * @returns {HTMLElement|null} novo card ou null se erro
   */
  function createVariantCard(product, variantData, originalCard, imageLoadingMode = 'lazy') {
    try {
      // Clone deep do card original
      const newCard = originalCard.cloneNode(true);

      // Marca como card criado pelo script E processado (evita loop infinito)
      newCard.setAttribute('data-variant-card', variantData.variantId);
      newCard.setAttribute('data-color', variantData.color);
      newCard.setAttribute('data-variant-processed', 'true');

      // ========== ATUALIZA IMAGEM COM OTIMIZAÇÃO ==========
      const imgElement = newCard.querySelector('.prod-img img');
      if (imgElement && variantData.image) {
        const imageUrl = typeof variantData.image === 'string'
          ? variantData.image
          : variantData.image.src;
        
        // Otimiza a URL da imagem para reduzir o tamanho do arquivo
        const optimizedUrl = optimizeImageUrl(imageUrl, CONFIG.imageSize);
        
        imgElement.src = optimizedUrl;
        imgElement.srcset = optimizedUrl;
        imgElement.alt = `${product.title} - ${variantData.color}`;
        
        const canUseEager = imageLoadingMode === 'eager' && !isCatalogTemplate();

        if (canUseEager) {
          imgElement.setAttribute('loading', 'eager');

          // Apenas o primeiro card de cor recebe alta prioridade de rede.
          if (STATE.renderedColorCardsCount === 0) {
            imgElement.setAttribute('fetchpriority', 'high');
          } else {
            imgElement.removeAttribute('fetchpriority');
          }
        } else {
          imgElement.setAttribute('fetchpriority', 'low');
          imgElement.loading = 'lazy';
        }

        imgElement.decoding = 'async';
      }

      // ========== ATUALIZA TÍTULO ==========
      const titleElement = newCard.querySelector('.prod-name');
      if (titleElement) {
        titleElement.textContent = `${product.title}`;
      }

      // ========== ATUALIZA PREÇO ==========
      const priceElement = newCard.querySelector('.prod-price');
      const hasVariantSale = Number(variantData.compare_at_price) > Number(variantData.price);
      if (priceElement) {
        priceElement.innerHTML =
          '<span class="por">a partir de</span> ' + formatBRL(variantData.price);
      }

      // O parcelamento vinha clonado do card original: uma cor sem desconto
      // exibia a parcela do preco promocional de outra cor.
      const installmentElement = newCard.querySelector('.prod-inst strong');
      if (installmentElement) {
        installmentElement.textContent = formatBRL(Math.round(Number(variantData.price) / 5));
      }

      // ========== ATUALIZA TARJA E PREÇO ORIGINAL POR VARIANTE ==========
      // O selo do clone e o do produto inteiro (o menor preco entre as cores),
      // entao sai sempre e so volta quando ESTA cor esta em promocao. A busca e
      // no card inteiro: o selo fica fora de .prod-badges, direto na foto.
      const saleTarget = newCard.querySelector('.prod-img') || newCard.querySelector('.prod-badges');
      newCard.querySelectorAll('.badge-sale').forEach(function (el) { el.remove(); });

      if (hasVariantSale && saleTarget) {
        const discount = Math.round(
          ((Number(variantData.compare_at_price) - Number(variantData.price)) * 100) /
          Number(variantData.compare_at_price)
        );
        const saleBadge = document.createElement('span');
        saleBadge.className = 'badge badge-sale';
        saleBadge.textContent = '-' + discount + '%';
        saleTarget.appendChild(saleBadge);
      }

      const originalPriceElement = newCard.querySelector('.prod-orig');
      if (originalPriceElement) {
        if (hasVariantSale) {
          const originalPriceInReais = (Number(variantData.compare_at_price) / 100).toFixed(2).replace('.', ',');
          originalPriceElement.textContent = `de R$ ${originalPriceInReais}`;
          originalPriceElement.style.display = '';
        } else {
          originalPriceElement.style.display = 'none';
        }
      }

      // ========== ATUALIZA LINK DO PRODUTO ==========
      const linkElement = newCard.querySelector('.prod-card-link');
      if (linkElement) {
        const baseUrl = linkElement.href.split('?')[0];
        linkElement.href = `${baseUrl}?${CONFIG.variantParam}=${variantData.variantId}`;
      }

      return newCard;
    } catch (error) {
      console.error('[VariantSplit] Erro ao criar card:', error);
      return null;
    }
  }

  /**
   * Expande um card de produto em múltiplos cards de cores
   * @param {HTMLElement} card - elemento do card do produto
   */
  function enqueueDeferredVariants(task) {
    if (!task || !task.remainingVariants || !task.remainingVariants.length) {
      return;
    }

    STATE.deferredVariantQueue.push(task);
    ensureDeferredQueueObserver();
    maybeDrainDeferredQueue();
  }

  function getDeferredGridContainer() {
    return (
      document.getElementById('ProductGrid') ||
      document.querySelector(CONFIG.collectionPageSelector) ||
      document.querySelector(CONFIG.homeSliderTrackSelector)
    );
  }

  function ensureDeferredQueueObserver() {
    if (STATE.deferredObserver || !('IntersectionObserver' in window)) {
      return;
    }

    const grid = getDeferredGridContainer();
    if (!grid || !grid.parentNode) {
      return;
    }

    let sentinel = document.getElementById('variant-split-deferred-sentinel');
    if (!sentinel) {
      sentinel = document.createElement('div');
      sentinel.id = 'variant-split-deferred-sentinel';
      sentinel.style.height = '1px';
      sentinel.setAttribute('aria-hidden', 'true');
      grid.parentNode.insertBefore(sentinel, grid.nextSibling);
    }

    STATE.deferredSentinel = sentinel;
    STATE.deferredObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target !== sentinel) return;
        STATE.deferredSentinelVisible = entry.isIntersecting;
        if (STATE.deferredSentinelVisible) {
          maybeDrainDeferredQueue();
        }
      });
    }, {
      root: null,
      rootMargin: resolveDeferredRootMargin(),
      threshold: 0.01,
    });

    STATE.deferredObserver.observe(sentinel);
  }

  function insertDeferredVariant(task) {
    if (!task || !task.remainingVariants || !task.remainingVariants.length) {
      return false;
    }

    if (!task.parentContainer || !task.parentContainer.isConnected) {
      return false;
    }

    const variantData = task.remainingVariants.shift();
    const newCard = createVariantCard(task.product, variantData, task.originalCard, 'lazy');
    if (!newCard) {
      return false;
    }

    if (task.isInSlider) {
      const newSliderCard = document.createElement('div');
      newSliderCard.className = 'slider-card';
      newSliderCard.appendChild(newCard);
      task.parentContainer.insertBefore(newSliderCard, task.currentInsertionPoint.nextSibling);
      task.currentInsertionPoint = newSliderCard;
    } else {
      task.parentContainer.insertBefore(newCard, task.currentInsertionPoint.nextSibling);
      task.currentInsertionPoint = newCard;
    }

    STATE.renderedColorCardsCount += 1;
    applyCatalogPageWindow();
    return true;
  }

  function drainDeferredQueue(maxCards = CONFIG.deferredInsertBatchSize) {
    let inserted = 0;

    while (inserted < maxCards && STATE.deferredVariantQueue.length > 0) {
      const task = STATE.deferredVariantQueue[0];
      const success = insertDeferredVariant(task);

      if (!success || task.remainingVariants.length === 0) {
        STATE.deferredVariantQueue.shift();
      }

      if (success) {
        inserted += 1;
      }
    }
  }

  function maybeDrainDeferredQueue() {
    if (STATE.isDrainingDeferredQueue || STATE.deferredVariantQueue.length === 0) {
      return;
    }

    // Mantem o carregamento inicial restrito. Demais cards so entram apos interacao de scroll.
    if (!STATE.isDeferredUnlocked) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      drainDeferredQueue(Number.MAX_SAFE_INTEGER);
      return;
    }

    if (!STATE.deferredSentinelVisible) {
      return;
    }

    STATE.isDrainingDeferredQueue = true;
    runWhenIdle(() => {
      drainDeferredQueue();
      STATE.isDrainingDeferredQueue = false;

      if (STATE.deferredSentinelVisible && STATE.deferredVariantQueue.length > 0) {
        maybeDrainDeferredQueue();
      }
    });
  }

  function unlockDeferredLoading() {
    if (STATE.isDeferredUnlocked) return;
    STATE.isDeferredUnlocked = true;

    if (STATE.deferredCardsBuffer.length) {
      processCardsWhenVisible(STATE.deferredCardsBuffer, { mode: 'visible' });
      STATE.deferredCardsBuffer = [];
    }

    maybeDrainDeferredQueue();
  }

  function ensureDeferredUnlockListener() {
    if (STATE.hasDeferredUnlockListener) return;
    STATE.hasDeferredUnlockListener = true;

    const onFirstScroll = () => {
      const scrolled = (window.scrollY || window.pageYOffset || 0) > 0;
      if (!scrolled) return;

      window.removeEventListener('scroll', onFirstScroll);
      unlockDeferredLoading();
    };

    window.addEventListener('scroll', onFirstScroll, { passive: true });

    // Home normalmente nao depende de scroll vertical para navegar cards.
    if (document.body.classList.contains('template-index')) {
      runWhenIdle(unlockDeferredLoading);
    }
  }

  async function expandProductCard(card, options = {}) {
    const mode = options.mode || 'visible';
    const isOffersMode = isOffersCollectionPage();

    // Protege o LCP no catalogo: o primeiro card permanece intacto no ciclo inicial.
    if (isCatalogTemplate() && mode === 'initial' && card.hasAttribute('data-lcp-candidate')) {
      return;
    }

    // Evita reprocessamento
    if (card.getAttribute('data-variant-processed') === 'true') {
      return;
    }

    // Marca como processado
    card.setAttribute('data-variant-processed', 'true');

    // Extrai handle do card
    const handle = extractProductHandle(card);

    if (!handle) {
      return;
    }

    // Verifica cache antes de fazer fetch
    let product;
    if (CONFIG.colorVariantsCache.has(handle)) {
      product = CONFIG.colorVariantsCache.get(handle);
    } else {
      product = await fetchProductVariants(handle);
      if (!product) return;
      CONFIG.colorVariantsCache.set(handle, product);
    }

    // Pega variantes de cor
    const colorVariants = getColorVariants(product);

    // Se tiver 0 ou 1 cor, não expande
    if (colorVariants.length === 0) {
      if (isOffersMode) {
        const sliderCard = card.closest('.slider-card');
        if (sliderCard) sliderCard.style.display = 'none';
        card.style.display = 'none';
      }
      applyCatalogPageWindow();
      return;
    }

    if (!isOffersMode && colorVariants.length <= 1) {
      applyCatalogPageWindow();
      return;
    }

    // Detecta se está dentro de um slider
    const sliderCard = card.closest('.slider-card');
    const isInSlider = sliderCard !== null;
    const insertionPoint = isInSlider ? sliderCard : card;
    const parentContainer = insertionPoint.parentNode;

    const shouldLimitByInitialBudget = mode === 'initial' && !isOffersMode;
    const availableBudget = shouldLimitByInitialBudget ? STATE.initialColorCardsBudget : colorVariants.length;
    const renderCount = Math.min(colorVariants.length, Math.max(availableBudget, 0));

    if (renderCount <= 0) {
      return;
    }

    // Cria novos cards para cada cor mantendo a ordem de cadastro
    let currentInsertionPoint = insertionPoint;
    colorVariants.slice(0, renderCount).forEach((variantData) => {
      const imageLoadingMode = STATE.renderedColorCardsCount < CONFIG.initialColorCardsLimit
        ? 'eager'
        : 'lazy';
      const newCard = createVariantCard(product, variantData, card, imageLoadingMode);
      if (!newCard) return;

      if (isInSlider) {
        // Se está em slider, clona o wrapper .slider-card
        const newSliderCard = document.createElement('div');
        newSliderCard.className = 'slider-card';
        newSliderCard.appendChild(newCard);
        parentContainer.insertBefore(newSliderCard, currentInsertionPoint.nextSibling);
        currentInsertionPoint = newSliderCard;
      } else {
        // Se está em grid, insere diretamente
        parentContainer.insertBefore(newCard, currentInsertionPoint.nextSibling);
        currentInsertionPoint = newCard;
      }

      STATE.renderedColorCardsCount += 1;
    });

    if (shouldLimitByInitialBudget) {
      STATE.initialColorCardsBudget = Math.max(0, STATE.initialColorCardsBudget - renderCount);
    }

    if (colorVariants.length > renderCount) {
      enqueueDeferredVariants({
        originalCard: card,
        product: product,
        remainingVariants: colorVariants.slice(renderCount),
        isInSlider: isInSlider,
        parentContainer: parentContainer,
        currentInsertionPoint: currentInsertionPoint,
      });
    }

    // ESCONDE o card original (pai) após criar as variantes
    if (isInSlider) {
      sliderCard.style.display = 'none';
    } else {
      card.style.display = 'none';
    }

    applyCatalogPageWindow();

    log(`Expandido: ${handle} em ${colorVariants.length} cores`);
  }

  /**
   * Processa cards em paralelo, respeitando limite de requisições
   * @param {Array<HTMLElement>} cards - elementos dos cards
   */
  async function processCardsInBatches(cards, options = {}) {
    const batches = [];
    for (let i = 0; i < cards.length; i += CONFIG.batchSize) {
      batches.push(cards.slice(i, i + CONFIG.batchSize));
    }

    for (const batch of batches) {
      await Promise.all(batch.map((card) => expandProductCard(card, options)));
    }
  }

  function processCardsWhenVisible(cards, options = {}) {
    const pending = cards.filter(
      (card) => card && card.isConnected && card.getAttribute('data-variant-processed') !== 'true'
    );

    if (!pending.length) return;

    if (isOffersCollectionPage()) {
      processCardsInBatches(pending, { mode: 'offers' });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      processCardsInBatches(pending, options);
      return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
      const visible = [];

      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const card = entry.target;
        obs.unobserve(card);
        visible.push(card);
      });

      if (visible.length) {
        processCardsInBatches(visible, options);
      }
    }, {
      root: null,
      rootMargin: resolveViewportRootMargin(),
      threshold: 0.01,
    });

    pending.forEach((card) => observer.observe(card));
  }

  function runWhenIdle(callback) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 1200 });
      return;
    }
    window.setTimeout(callback, 50);
  }

  function isInInitialViewport(card) {
    if (!card) return false;
    const rect = card.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

    // Prioriza cards no viewport e proximos ao fold para evitar atraso visual.
    return rect.top < viewportHeight * 1.5 && rect.bottom > 0;
  }

  /**
   * Inicializa o script: encontra todos os cards e os expande
   */
  async function init() {
    log('Iniciando Variant Split');

    STATE.initialColorCardsBudget = resolveInitialColorCardsLimit();
    STATE.catalogVisibleLimit = CONFIG.catalogCardsPerPage;

    // Seleciona todos os cards de produto na página
    const cards = Array.from(document.querySelectorAll(CONFIG.productCardSelector));

    if (cards.length === 0) {
      log('Nenhum card de produto encontrado');
      return;
    }

    log(`Encontrados ${cards.length} cards`);

    if (isOffersCollectionPage()) {
      await processCardsInBatches(cards, { mode: 'offers' });
      applyCatalogPageWindow();
      return;
    }

    const initialCandidates = cards.filter((card) => isInInitialViewport(card));

    if (!initialCandidates.length && cards.length) {
      // Fallback para o topo da lista caso medicao de viewport falhe cedo.
      initialCandidates.push(...cards.slice(0, Math.min(cards.length, CONFIG.batchSize)));
    }

    for (let i = 0; i < initialCandidates.length; i += 1) {
      if (STATE.initialColorCardsBudget <= 0) {
        break;
      }

      await expandProductCard(initialCandidates[i], { mode: 'initial' });
    }

    const deferredCards = cards.filter(
      (card) => card && card.isConnected && card.getAttribute('data-variant-processed') !== 'true'
    );

    if (deferredCards.length) {
      // Restante so libera apos scroll real do usuario para proteger LCP inicial.
      STATE.deferredCardsBuffer = deferredCards;
      ensureDeferredUnlockListener();
    }

    maybeDrainDeferredQueue();
    applyCatalogPageWindow();
  }

  /**
   * Monitora mudanças no DOM para detectar novos cards (lazy load, infinite scroll, etc)
   */
  function observeNewCards() {
    const pendingCards = new Set();
    let flushTimer = null;

    function flushPendingCards() {
      flushTimer = null;
      const cards = Array.from(pendingCards).filter(
        (card) => card && card.isConnected && !card.getAttribute('data-variant-card')
      );
      pendingCards.clear();

      if (cards.length > 0) {
        if (isOffersCollectionPage()) {
          processCardsInBatches(cards, { mode: 'offers' });
          runWhenIdle(() => {
            applyCatalogPageWindow();
          });
          return;
        }

        if (STATE.isDeferredUnlocked) {
          processCardsWhenVisible(cards, { mode: 'visible' });
        } else {
          STATE.deferredCardsBuffer = STATE.deferredCardsBuffer.concat(cards);
          ensureDeferredUnlockListener();
        }

        runWhenIdle(() => {
          applyCatalogPageWindow();
        });
      }
    }

    function queueCard(card) {
      if (!card || card.getAttribute('data-variant-card')) return;
      pendingCards.add(card);

      if (flushTimer) return;
      flushTimer = window.requestIdleCallback
        ? window.requestIdleCallback(flushPendingCards, { timeout: 300 })
        : window.setTimeout(flushPendingCards, 50);
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // IGNORA cards criados pelo próprio script
            if (node.getAttribute?.('data-variant-card')) {
              return;
            }
            
            // IGNORA slider-cards que contêm cards criados pelo script
            if (node.classList?.contains('slider-card')) {
              const hasVariantCard = node.querySelector('[data-variant-card]');
              if (hasVariantCard) return;
            }
            
            // Verifica se o nó adicionado é um card
            if (node.matches?.(CONFIG.productCardSelector)) {
              queueCard(node);
            }
            // Verifica filhos do nó adicionado
            node.querySelectorAll?.(CONFIG.productCardSelector).forEach((card) => {
              // Ignora cards que já são clones
              if (!card.getAttribute('data-variant-card')) {
                queueCard(card);
              }
            });
          }
        });
      });
    });

    const targets = [];
    const collectionGrid = document.querySelector(CONFIG.collectionPageSelector);
    if (collectionGrid) targets.push(collectionGrid);

    const sliderTracks = document.querySelectorAll('.slider-track');
    sliderTracks.forEach((track) => targets.push(track));

    if (targets.length === 0) {
      targets.push(document.body);
    }

    targets.forEach((target) => observer.observe(target, {
      childList: true,
      subtree: true,
    }));

    log('MutationObserver ativo para novos cards');
  }

  // ============================================
  // INICIALIZAÇÃO
  // ============================================

  function boot() {
    if (window.Shopify && window.Shopify.designMode) {
      return;
    }

    const isSupportedTemplate =
      document.body.classList.contains('template-index') ||
      document.body.classList.contains('template-collection') ||
      document.body.classList.contains('template-search');

    if (!isSupportedTemplate) return;
    ensureCatalogLoadMoreBridge();
    init();
    observeNewCards();
  }

  if (document.readyState === 'complete') {
    runWhenIdle(boot);
  } else {
    // Aguarda recursos criticos carregarem antes de iniciar o split de variantes.
    window.addEventListener('load', () => {
      runWhenIdle(boot);
    }, { once: true });
  }
})();

